import { z } from "zod";

import { apiClient } from "./api";
// Reused verbatim rather than redefined here — see `TurnSharePreviewResponse`
// below: the backend's turn-sharing preview endpoint emits the exact same
// `SafeVisualRelationProjection` wire shape `reports.ts` already models for
// report sharing (`return-knowledge-images-across-surfaces` PR 4, one shared
// projection type for both surfaces). A second, independently-typed copy
// here would be exactly the kind of two-sources-that-happen-to-agree this
// task closes on the backend.
import {
  MAX_REPORT_ASSETS,
  publicVisualReadCapabilitySchema,
  safeVisualRelationProjectionSchema,
} from "./reports";

/**
 * Sharing a single chat turn (`add-chat-turn-sharing`): the session owner
 * creates a revocable grant — `beflow_team` (any authenticated account) or
 * `anyone_with_link` (public capability URL) — with a fixed scope
 * (`question_and_answer` or `answer_only`) chosen at creation time.
 */

export const turnShareAudienceSchema = z.enum(["beflow_team", "anyone_with_link"]);
export type TurnShareAudience = z.infer<typeof turnShareAudienceSchema>;

export const turnShareScopeSchema = z.enum(["question_and_answer", "answer_only"]);
export type TurnShareScope = z.infer<typeof turnShareScopeSchema>;

export const turnShareGrantReadSchema = z
  .object({
    grant_id: z.guid(),
    audience: turnShareAudienceSchema,
    scope: turnShareScopeSchema,
    token_prefix: z.string().nullable(),
    created_at: z.string(),
    revoked_at: z.string().nullable(),
  })
  .strict();
export type TurnShareGrantRead = z.infer<typeof turnShareGrantReadSchema>;

export const createTurnShareResponseSchema = z
  .object({
    grant: turnShareGrantReadSchema,
    url: z.string().min(1),
    token: z.string().nullable(),
  })
  .strict();
export type CreateTurnShareResponse = z.infer<typeof createTurnShareResponseSchema>;

export const publicTurnViewSchema = z
  .object({
    question: z.string().nullable(),
    answer: z.string(),
    created_at: z.string(),
    assets: z.array(safeVisualRelationProjectionSchema).max(MAX_REPORT_ASSETS).default([]),
    visual_read_capabilities: z
      .array(publicVisualReadCapabilitySchema)
      .max(MAX_REPORT_ASSETS)
      .default([]),
  })
  .strict();
export type PublicTurnView = z.infer<typeof publicTurnViewSchema>;

// Raw wire shape of one `chat_messages.citations` entry (arbitrary JSON
// object; the backend persists whatever the citation registry recorded).
// `teamCitationToCitation` below adapts it to `Citation` defensively —
// see `@/components/chat/CitationCard` — for rendering with `SourcesBlock`.
const teamTurnCitationSchema = z.record(z.string(), z.unknown());
export type TeamTurnCitation = z.infer<typeof teamTurnCitationSchema>;

export const teamTurnViewSchema = z
  .object({
    grant_id: z.guid(),
    question: z.string().nullable(),
    answer: z.string(),
    citations: z.array(teamTurnCitationSchema).nullable(),
    created_at: z.string(),
    assets: z.array(safeVisualRelationProjectionSchema).max(MAX_REPORT_ASSETS).default([]),
  })
  .strict();
export type TeamTurnView = z.infer<typeof teamTurnViewSchema>;

/**
 * The exact visual projection a grant created with this audience/scope
 * would produce, WITHOUT creating one — mirrors `reports.ts`'s
 * `sharePreviewResponseSchema` (`GET .../share/preview`).
 * `return-knowledge-images-across-surfaces` PR 4: closes the spec
 * requirement that the owner sees the exact anonymous projection before
 * activating a turn grant, backed by the SAME backend projection functions
 * `resolve_public`/`resolve_team` use — never a frontend re-derivation of
 * what "should" be redacted.
 */
export const turnSharePreviewResponseSchema = z
  .object({
    audience: turnShareAudienceSchema,
    scope: turnShareScopeSchema,
    assets: z.array(safeVisualRelationProjectionSchema),
    included_asset_count: z.number().int().min(0),
    redacted_asset_count: z.number().int().min(0),
  })
  .strict();
export type TurnSharePreviewResponse = z.infer<typeof turnSharePreviewResponseSchema>;

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** Defensive adapter from the raw citation JSON to `Citation` — the shape
 * `SourcesBlock` (`@/components/chat/CitationCard`) expects. Missing
 * fields fall back to empty strings rather than failing the whole page. */
export function teamCitationToCitation(raw: TeamTurnCitation): {
  id: string;
  title: string;
  preview: string;
  source: string;
  source_ref: string;
  external_url: string;
} {
  return {
    id: stringField(raw.id),
    title: stringField(raw.title) || "Fuente",
    preview: stringField(raw.preview),
    source: stringField(raw.source),
    source_ref: stringField(raw.source_ref),
    external_url: stringField(raw.external_url),
  };
}

function turnSharesBase(sessionId: string, assistantMessageId: string): string {
  return `/chat/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(assistantMessageId)}/shares`;
}

export function createTurnShare(args: {
  sessionId: string;
  assistantMessageId: string;
  audience: TurnShareAudience;
  scope: TurnShareScope;
}): Promise<CreateTurnShareResponse> {
  return apiClient.post(
    turnSharesBase(args.sessionId, args.assistantMessageId),
    createTurnShareResponseSchema,
    { audience: args.audience, scope: args.scope },
  );
}

export function previewTurnShare(args: {
  sessionId: string;
  assistantMessageId: string;
  audience: TurnShareAudience;
  scope: TurnShareScope;
}): Promise<TurnSharePreviewResponse> {
  return apiClient.get(
    `${turnSharesBase(args.sessionId, args.assistantMessageId)}/preview`,
    turnSharePreviewResponseSchema,
    { query: { audience: args.audience, scope: args.scope } },
  );
}

export function listTurnShares(args: {
  sessionId: string;
  assistantMessageId: string;
}): Promise<TurnShareGrantRead[]> {
  return apiClient.get(
    turnSharesBase(args.sessionId, args.assistantMessageId),
    z.array(turnShareGrantReadSchema),
  );
}

export function revokeTurnShare(grantId: string): Promise<void> {
  return apiClient.delete(`/chat/shares/${encodeURIComponent(grantId)}`, z.void());
}

/** Public resolve — no auth header; capability only in POST body. */
export async function resolvePublicTurn(token: string): Promise<PublicTurnView> {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const response = await fetch(`${baseUrl}/shared/turn/resolve`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
    credentials: "omit",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`resolve_turn_failed_${response.status}`);
  }
  return publicTurnViewSchema.parse(await response.json());
}

export function resolveTeamTurn(grantId: string): Promise<TeamTurnView> {
  return apiClient.get(`/shared/team/turns/${encodeURIComponent(grantId)}`, teamTurnViewSchema);
}
